import React, { useCallback, useEffect, useState } from "react";

import ErrorBoundary from "antd/es/alert/ErrorBoundary";

import { addOrUpdateVerificationTask, getAllVerificationTasks, getSettings } from "services";

import { reverseCamelizeString, useCancelAsyncOnUnmount, useNavigate, useUpdateAdminSettings } from "utils";

import {
  AdminHead,
  Button,
  CTAButton,
  Checkbox,
  Col,
  FilterSet,
  Form,
  IconButton,
  InputNumber,
  MainPage,
  Row,
  Spin,
  Table,
  TablePanel,
  Tabs,
  TagNormal,
  Typography,
  Message as message,
} from "components";

import { VerificationsTaskDrawer } from "pages/admin/verifications/VerificationsTaskDrawer";

export const VerificationsTaskList = () => {
  const [deadline, setDeadline] = useState();
  const [filter, setFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [settings, setSettings] = useState();
  const [showForm, setShowForm] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [tasksCount, setTasksCount] = useState(0);
  const [selectedTask, setSelectedTask] = useState(null);

  const defaultItemsPerPage = 10;
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  const navigate = useNavigate();
  const signal = useCancelAsyncOnUnmount()();

  const { mutateAsync: updateAdminSettings } = useUpdateAdminSettings();

  const getTasks = useCallback(() => {
    getAllVerificationTasks({
      signal,
      filter,
      page: page - 1,
      itemsPerPage,
    }).then((res) => {
      setTasks(res.tasks);
      setTasksCount(res.count);
      setLoading(false);
    });
  }, [signal, filter, itemsPerPage, page]);

  const loadSettings = useCallback(() => {
    getSettings({
      signal,
    }).then((res) => setSettings(res));
  }, [signal]);

  const handleCloseDrawer = () => {
    setSelectedTask(null);
    setShowForm(false);
    getTasks();
  };

  const toggleDisableTask = (id, disabled) => {
    const task = tasks.find((task) => task.id === id);

    if (!task) {
      return;
    }

    addOrUpdateVerificationTask({
      signal,
      ...task,
      id,
      disabled,
    }).then(() => {
      message({
        content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
        icon: "gremlin",
      });
      getTasks();
    });
  };

  const openEditForm = ({ taskId }) => {
    const task = tasks.find(({ id }) => id === taskId);

    if (task) {
      setSelectedTask(task);
      setShowForm(true);
    }
  };

  const openAddForm = () => {
    setShowForm(true);
  };

  const submitSettingsForm = () => {
    updateAdminSettings({
      ...settings,
      defaultVerificationDeadline: JSON.stringify(deadline),
    }).then(() => {
      message({
        content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
        icon: "gremlin",
      });
      loadSettings();
    });
  };

  useEffect(() => {
    getTasks();
    loadSettings();
  }, [getTasks, loadSettings]);

  useEffect(() => {
    if (settings) {
      setDeadline(settings.defaultVerificationDeadline);
    }
  }, [settings]);

  const tableData = tasks.map(({ id, documentName, defaultTask, practiceSlugs, country, disabled }) => ({
    key: id,
    className: disabled ? "disabled-task-row" : "",
    enabled: <Checkbox defaultChecked={!disabled} onChange={(e) => toggleDisableTask(id, !e.target.checked)} />,
    "document name": <TablePanel headline={documentName} dataTestId={`verifications-tasks-${documentName}`} />,
    "sending setting": defaultTask ? "automatic" : "manual",
    country: country,
    practices: (
      <div className="assigned-practices-row">
        {practiceSlugs.map((slug) => (
          <TagNormal className="margin-right-25" key={slug} text={reverseCamelizeString(slug)} />
        ))}
      </div>
    ),
    actions: (
      <IconButton
        dataTestId={`task-${documentName}-edit`}
        type="secondary"
        tooltip={{ title: "edit this task", placement: "top" }}
        iconName="tabler/edit"
        onClick={() => openEditForm({ taskId: id })}
      />
    ),
  }));

  const handlePageChange = (page, pageSize) => {
    setPage(page);
    setItemsPerPage(pageSize);
  };

  const handleFilterChange = (filter) => {
    setFilter(filter);
    setPage(1);
  };

  const filterSettings = {
    selectedFilters: [...filter],
    onClearFilters: () => handleFilterChange([]),
    onSelectFilter: (key) => handleFilterChange([key]),
    onUnselectFilter: () => handleFilterChange([]),
    filters: {
      defaultTask: {
        title: "default due date",
      },
      customTask: {
        title: "custom due date",
      },
    },
  };

  return (
    <MainPage loading={false} pageName="admin">
      <div className="margin-bottom-200 verification-tasks-link">
        <IconButton dataTestId="task-list-back-button" iconName="tabler/arrow-left" type="secondary" onClick={() => navigate("/verifications/farms")} />
        <Typography variant="h6" className="margin-left-100">
          Back to farm list
        </Typography>
      </div>
      <AdminHead headline="Verification Tasks" description="add and update verification tasks here" />
      <Tabs
        items={[
          {
            dataTestId: "task-list-manage-tasks",
            label: "Manage verification tasks",
            key: "tasks",
            children: !loading ? (
              <Row className="padding-top-250">
                <Col>
                  <CTAButton dataTestId="task-list-add-new-task" text="Set a new task" onClick={() => openAddForm()} />
                </Col>
                <Col className="margin-top-100 margin-bottom-100">
                  <Typography variant="h3">{tasksCount} verification tasks</Typography>
                </Col>
                <Col className="margin-bottom-100">
                  <FilterSet {...filterSettings} />
                </Col>
                {!tasks.length ? (
                  <Col>
                    <Typography variant="h6">No tasks have been added</Typography>
                  </Col>
                ) : (
                  <Col>
                    <Table
                      data={tableData}
                      className="verifications-tasks-table"
                      loading={loading}
                      rowClassName={(record) => record.className}
                      pagination={{
                        pageSizeOptions: [10, 20],
                        total: tasksCount,
                        onChange: handlePageChange,
                        defaultPageSize: defaultItemsPerPage,
                        pageSize: itemsPerPage,
                      }}
                    />
                  </Col>
                )}
              </Row>
            ) : (
              <Row>
                <Col className="center">
                  <Spin />
                </Col>
              </Row>
            ),
          },
          {
            dataTestId: "task-list-set-deadline",
            label: "Set verification deadline",
            key: "deadline",
            children: !loading ? (
              <Row className="mini-form">
                <Col className="padding-top-250">
                  <Form>
                    <Typography variant="paragraph-m">
                      This is the default deadline, in days from the date set, for all automatically generated tasks
                    </Typography>
                    <InputNumber
                      suffix="days"
                      label="Default task deadline"
                      name="defaultVerificationDeadline"
                      defaultValue={deadline}
                      rules={[{ required: true }]}
                      onChange={(v) => setDeadline(v)}
                      className="margin-bottom-200 margin-top-100"
                    />

                    <Button type="primary" onClick={() => submitSettingsForm()}>
                      Submit data
                    </Button>
                  </Form>
                </Col>
              </Row>
            ) : (
              <Spin />
            ),
          },
        ]}
      />
      <ErrorBoundary>
        <VerificationsTaskDrawer task={selectedTask} visible={showForm} onClose={handleCloseDrawer} />
      </ErrorBoundary>
    </MainPage>
  );
};
